import { isPlatformBrowser, Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  input,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { faPlay, faTimes, faDownload } from '@fortawesome/free-solid-svg-icons';
import { first, lastValueFrom, map, Subject, takeUntil } from 'rxjs';
import { SwiperOptions } from 'swiper/types';

import { AssetType, CarouselTypeParams, ENVIRONMENT, Environment } from '@environment';
import { CompetitionsService } from '@usheru-hff/movie-detail/feature-competitions';
import { LogoSizes } from '@usheru-hff/movie-detail/feature-movie-hero';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { LocationService } from '@usheru-hff/sdk/location';
import { MovieService } from '@usheru-hff/sdk/movie';
import { TRACKING_SERVICE, TrackingService } from '@usheru-hff/sdk/tracking';
import {
  BrandedSection,
  Channel,
  ConsumerLead,
  CustomElementsTemplateB,
  CustomModule,
  CustomMovie,
  getMovieGenres,
  getMovieTags,
  Movie,
  MovieBrandedSection,
  MovieLaurel,
  PageLink,
  ShowtimesTab,
  TabType,
  UserRole
} from '@usheru-hff/shared/domain';
import { FooterService } from '@usheru-hff/shared/services';
import { VideoFullscreenService } from '@usheru-hff/shared/ui-video';
import { BrandedPageThemeService, TemplatesService } from '@usheru-hff/shared/utils';
import { ImageService } from '@usheru-hff/shared/utils-image';
import { MetaTags, MetatagsService } from '@usheru-hff/shared/utils-metatag';
import { ShowtimeStreamingService } from '@usheru-hff/shared/utils-movie';
import { PageLoaderService } from '@usheru-hff/shared/utils-navigation';
import { TitleService } from '@usheru-hff/shared/utils-title';
import { TranslationsService } from '@usheru-hff/shared/utils-translation';
import { UplayerService } from '@usheru-hff/uplayer/domain';

import { MOVIE_TEMPLATE_ANIMATIONS } from './movie-template-b.animations';

// type Tab = 'showtimes' | 'streaming' | 'movieinfo' | 'trailer' | 'about' | 'gallery';
// const tabs: Tab[] = ['showtimes', 'streaming', 'movieinfo', 'trailer'];

@Component({
    selector: 'ush-movie-template-b',
    templateUrl: './movie-template-b.component.html',
    styleUrls: ['./movie-template-b.component.scss'],
    animations: MOVIE_TEMPLATE_ANIMATIONS,
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class MovieTemplateBComponent implements OnInit, OnDestroy {
  @ViewChild('homeTab', { read: TemplateRef }) homeTab: TemplateRef<unknown>;

  protected readonly specialTextLabel = $localize`:@@branded-page_showtimes-special-text:`;

  @ViewChild('horizontal', { static: false })
  horizontalTemp: TemplateRef<any>;

  @ViewChild('vertical', { static: false })
  verticalTemp: TemplateRef<any>;

  @Input()
  updateUrl = true;

  _customMovie: CustomMovie;
  get customMovie() {
    return this._customMovie;
  }
  @Input() set customMovie(value: CustomMovie) {
    this.seeMorePos = value?.seeMorePos;
    this.seeMoreType = value?.seeMoreType;
    this._customMovie = value;
    this.hideCompetition = value?.hideCompetition;
    this.menuTrailerCustomTemplate = value?.menuTrailerCustomTemplate;
    this.hideWatchNowCtaButton = value?.hideWatchNowCtaButton;
  }

  @Input() synopsisTopAddon?: TemplateRef<any>;

  @Input() movieMenuExtraElements: TemplateRef<unknown>;

  channel: Channel;
  loaded = false;

  menuTrailerCustomTemplate: TemplateRef<any>;
  // params: Params;
  queryParams: Params;
  customMovieLogo: string;
  template: string;
  tab = 'home';
  arrowTemplate: number;
  hideArrows: boolean;

  extraControlsContent: TemplateRef<any>[] = [];

  responsiveOptions: any[] = [
    {
      breakpoint: '1024px',
      numVisible: 5
    },
    {
      breakpoint: '960px',
      numVisible: 4
    },
    {
      breakpoint: '768px',
      numVisible: 3
    },
    {
      breakpoint: '560px',
      numVisible: 1
    }
  ];

  channelName: string;
  disableShowtimes = false;
  disableStreaming = false;
  trailerEmbed = false;
  trailerAutoplay = false;
  showtimesFound: boolean;
  streamingAvailable: boolean;
  showtimesAvailable: boolean;
  hideWatchNowCtaButton: boolean;
  compactInfo: boolean;
  infoExpanded: boolean;
  hasCompetitions = false;
  hideCompetition = false;
  hideCompetitionBtn: boolean;
  carouselTypeParams: CarouselTypeParams;
  channelSocialNetworks: boolean;
  hasReviews: boolean;
  competitionBanner: string;
  displayGallery: boolean;
  displayPosters: boolean;
  displayVideos: boolean;
  displayPoster: boolean;
  customCompetitionCta: string;
  videoThumbnailPlayIcon: AssetType;
  streamingPlatformsLoadedTracked: boolean;

  //form variables
  sentIt = false;
  faTimes = faTimes;
  faDownload = faDownload;

  // Templates refs
  brandedInCinemasAddon: TemplateRef<any>;
  extraContent: { type: string; module: BrandedSection | CustomModule }[];
  topBanner: CustomModule;
  customSubscribeMessage: string;
  trailerInMenu: boolean;
  homeInMenu: boolean;
  /**
   * It adds the logo on the custom movie template header.
   * Be aware to hide the main header logo to don't duplicate them.
   * Only used on la-guitarra-flamenca-de-yerai-cortes.
   * */
  logoInMenu: boolean;

  extraTabs: { key: string; url?: string; extraSection?: string; external?: boolean; function?: any }[];
  hiddenBrandedSections: MovieBrandedSection[];
  hiddenShowtimesTabs: ShowtimesTab[];
  mobile: boolean;
  faPlay = faPlay;

  seeMorePos?: 'top' | 'middle' | 'buttons' | 'none';
  seeMoreType?: number;
  beforeReleaseBookBtnCustom: { id: number; text?: string; url?: string; date?: Date }[];

  customElements!: CustomElementsTemplateB;
  desktopWallpapers;
  mobileWallpapers;

  /**
   * Default value is true to try render the component but the true value will be setted after the first request to the api
   */
  socialFeedFound = true;

  usePlaceholderPerformancesGroup = false;

  festivalsEnabled: boolean;
  festivals: MovieLaurel[];

  qParams: Params;

  movie: Movie;

  logoSizes: LogoSizes | undefined;

  locReleaseDate$ = this.movieService.movie$.pipe(
    map(movie => parseInt(movie.locReleaseDate || movie?.releaseDate) || 0)
  );
  today = new Date().getTime();

  appLogoUrl = this.channelService.channel.logoUrl;
  appLogoParams?: { width?: number; height?: number };

  destroy$ = new Subject<void>();

  constructor(
    public videoPlayerService: VideoFullscreenService,
    @Inject(ENVIRONMENT) private env: Environment,
    @Inject(PLATFORM_ID) private platform: any,
    private router: Router,
    private route: ActivatedRoute,
    private channelService: ChannelService,
    private movieService: MovieService,
    private titleService: TitleService,
    private brandedThemeService: BrandedPageThemeService,
    private imgService: ImageService,
    private locationService: LocationService,
    private location: Location,
    private metaTagService: MetatagsService,
    private translationsService: TranslationsService,
    private templatesService: TemplatesService,
    @Inject(TRACKING_SERVICE) private trackingService: TrackingService,
    private competitionsService: CompetitionsService,
    private pageLoader: PageLoaderService,
    private footerService: FooterService,
    private uplayerService: UplayerService,
    private showtimeStreamingService: ShowtimeStreamingService,
    private cd: ChangeDetectorRef
  ) {
    this.usePlaceholderPerformancesGroup = !!this.env?.placeholderData?.performancesGroup || false;
    //
    const channelSettings = this.channelService.channel.settings;
    this.channel = this.channelService.channel;
    this.channelName = this.channelService.channel.name;
    this.disableShowtimes = channelSettings.disableShowtimes;
    this.disableStreaming = channelSettings.disableStreaming;
    this.brandedInCinemasAddon = this.templatesService.get('brandedInCinemasAddon');

    this.setAppLogoParams();
  }

  onDisplayComponent(elem) {
    if (elem == 'gallery') this.displayGallery = true;
    if (elem == 'videos') this.displayVideos = true;
    if (elem == 'posters') this.displayPosters = true;
  }

  ngOnInit(): void {
    this.trailerInMenu = this.customMovie.trailerInMenu;
    this.homeInMenu = this.customMovie.homeInMenu;
    this.logoInMenu = this.customMovie.logoInMenu;

    this.customCompetitionCta = this.customMovie.customCompetitionCta;
    this.customMovieLogo = this.customMovie.customMovieLogo;
    this.desktopWallpapers = this.customMovie.wallpapers?.desktopWallpaper;
    this.mobileWallpapers = this.customMovie.wallpapers?.mobileWallpaper;
    this.template = this.customMovie.template;
    this.seeMorePos = this.customMovie.seeMorePos;
    this.seeMoreType = this.customMovie.seeMoreType;
    if (this.customMovie.beforeReleaseBookBtnCustom) {
      this.beforeReleaseBookBtnCustom = this.customMovie.beforeReleaseBookBtnCustom.map((l, i) => ({
        id: i,
        text: l.text,
        url: l.url,
        date: l.date
      }));
    }

    //
    this.festivalsEnabled = !!this.customMovie.festivals;
    this.festivals = this.customMovie.festivals;
    this.defineGalleryArrowTemplate();
    this.setUpTemplateProperties();
    //
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(qParams => {
      this.qParams = Object.assign(qParams, {});
      if (!this.loaded) {
        // fix margin/padding header
        //document.getElementById('header').style.marginBottom = '0px';
        // document.getElementById('router-container').style.paddingTop = '0px'; //todo: add it in app.compoennt.html first

        if (!this.customMovie.template) {
          this.customMovie.template = 'default';
        }
        if (this.customMovie.template === 'vertical') {
          this.updateUrl = false;
        }

        if (this.customMovie?.transparentFooter) {
          // (<HTMLElement>document.getElementsByClassName('footer-wrapper')[0]).style.backgroundColor = 'transparent';
        }
        this.setRottenTomatoes();
        this.setLetterboxd();

        if (this.customMovie.socialNetworkLinksFooter)
          this.footerService.setCustomSocialLinks(this.movie?.socialNetworks);
        this.channelSocialNetworks = this.customMovie.replaceMovieNetworksByChannelNetworks;
        this.menuTrailerCustomTemplate = this.customMovie.menuTrailerCustomTemplate;
        this.trailerEmbed = this.customMovie.trailerEmbed;
        this.trailerAutoplay = this.customMovie.trailerAutoplay;
        this.extraControlsContent = this.customMovie.extraControlsContent;
        this.loadExtraContent();
        this.extraTabs = this.customMovie?.extraTabs;
        this.compactInfo = this.customMovie?.compactInfo;
        this.carouselTypeParams = this.customMovie?.carouselTypeParams;
        this.customSubscribeMessage = this.customMovie?.customSubscribeMessage;
        this.videoThumbnailPlayIcon = this.customMovie?.customAssets?.videoThumbnailPlayIcon;
        this.logoSizes = this.customMovie?.logoSizes;

        this.loadMovie();
      }
    });
  }

  loadMovie() {
    const getMovie$ = this.movieService
      .getMovie(
        this.customMovie.slug,
        this.translationsService.currentLanguage().code,
        this.locationService.selectedCountry.slug
      )
      .pipe(first());

    lastValueFrom(getMovie$)
      .then(movie => {
        this.initMovie(movie);
        if (this.isBrowser) {
          try {
            setTimeout(() => {
              this.scrollToSection(this.qParams.section);
            }, 1000);
          } catch (e) {
            console.error(e);
          }
        }
      })
      .catch(err => {
        this.loaded = true;
        console.error(err);
      });
  }

  setUpTemplateProperties() {
    if (!this.isBrowser) return;
    if (this.customMovie.customFooterStyle) this.footerService.setCustomStyle(this.customMovie.customFooterStyle);
  }

  initMovie(movie: Movie) {
    this.movie = { ...movie } as Movie;
    this.setMetaTags(movie);
    this.setInitialTracking();
    this.loaded = true;
    this.evaluateReviews(movie);
    this.evaluateUplayer(movie);
    this.setStreamingAvailability();
    this.setShowtimesAvailability();
    //
    let section = this.qParams.section || this.qParams.whereToWatch;
    if (this.template === 'horizontal') {
      section = section?.replace('in-cinemas', 'showtimes')?.replace('watch-at-home', 'streaming');

      if (!section && this.showtimesAvailable) {
        section = 'showtimes';
      }

      if (!section) section = 'home';
    }
    this.goTo(section);
    //
    const competitionBanner = this.customMovie.competitionBanner;
    if (competitionBanner) {
      this.competitionBanner =
        typeof competitionBanner === 'boolean' ? `/competitions/${this.movie.id}` : competitionBanner;
    }
    if (this.env.competitions) {
      this.competitionsService.getCompetition(this.movie.id).subscribe(competition => {
        this.hasCompetitions = !!competition;
        this.provideAvailablePageLinksToMobileMenu();
      });
    } else {
      this.provideAvailablePageLinksToMobileMenu();
    }
    this.cd.detectChanges();
  }

  ngOnDestroy() {
    this.titleService.setFavicon();
    this.metaTagService.resetMetaTags();

    this.brandedThemeService.setBrandedMovie(null);
    this.brandedThemeService.setBrandedPageLinks([]);
    this.brandedThemeService.setBrandedMobileMenuStyle(null);

    this.footerService.restoSocialLinks();
    this.footerService.removeCustomStyle();
    //
    this.destroy$.next();
    this.destroy$.complete();
  }

  evaluateUplayer(movie: Movie) {
    if (movie.hasPlayer) this.uplayerService.retrieveAvailablePlayerPrices(movie.id);
  }

  private defineGalleryArrowTemplate() {
    this.hideArrows = this.customMovie.carouselTypeParams?.gallery?.hideArrows;
    this.arrowTemplate =
      this.customMovie.carouselTypeParams?.gallery?.arrowLayoutDesing?.top ||
      this.customMovie.carouselTypeParams?.gallery?.arrowLayoutDesing?.down ||
      this.customMovie.carouselTypeParams?.gallery?.arrowLayoutDesing?.inside ||
      this.customMovie.carouselTypeParams?.gallery?.arrowLayoutDesing?.side;
  }

  private setInitialTracking() {
    this.trackingService.trackEvent({
      action: 'FilmPageView',
      properties: {
        category: 'MovieDetail',
        label: this.movie.title,
        movie: this.movie.title,
        idMovie: this.movie.id,
        imdb: this.movie?.imdb,
        tagsMovie: getMovieTags(this.movie),
        genresMovie: getMovieGenres(this.movie)
      }
    });
  }

  loadExtraContent() {
    const fromCustomMovie =
      this.customMovie.extraContentSection?.map(module => ({
        type: 'custom-movie',
        module
      })) || [];
    const fromChannel =
      this.channelService.channel.getCustomModules({ startWith: `${this.customMovie.slug}:` })?.map(module => {
        module.title = module.title.replace(`${this.customMovie.slug}:`, '');
        return {
          type: 'channel',
          module
        };
      }) || [];

    const topBannerTitle = `top-banner`;
    const topBanner = fromChannel.find(c => c.module.title == topBannerTitle);
    if (topBanner) {
      this.topBanner = topBanner.module;
      fromChannel.splice(fromChannel.indexOf(topBanner), 1);
    }

    this.extraContent = [...fromCustomMovie, ...fromChannel];
  }

  setRottenTomatoes() {
    if (this.customMovie?.rottentomato) this.movie.rottentomato = this.customMovie.rottentomato;
  }

  setLetterboxd() {
    if (this.customMovie?.letterboxd) this.movie.letterboxd = this.customMovie.letterboxd;
  }

  evaluateReviews(movie: Movie) {
    if (movie.reviews) {
      this.hasReviews = this.customMovie.enableReviews;
    }
  }
  setMetaTags(movie: Movie): void {
    const metaTags: MetaTags = {
      url: this.location.path(),
      title: this.translationsService.keyExists(`movie.${movie.friendlyUrl}.title`)
        ? `movie.${movie.friendlyUrl}.title`
        : $localize`:@@meta.page.movie-detail.title:_channel_ - _movie_`,
      description: this.translationsService.keyExists(`movie.${movie.friendlyUrl}.description`)
        ? `movie.${movie.friendlyUrl}.description`
        : $localize`:@@meta.page.movie-detail.description:Description`,
      video: movie.trTrailerLink
    };

    metaTags.favicon = movie?.faviconImageUrl;

    if (movie.tilePhotoUrl) {
      metaTags.image = this.imgService.computeUrl(movie.tilePhotoUrl, { width: 1280 });
    } else if (movie.portraitPhotoUrl) {
      metaTags.image = this.imgService.computeUrl(movie.portraitPhotoUrl, { height: 1000 });
    }

    //TODO cases with more directors?
    const director = movie?.movieCrew?.find(mc => mc.crewRole.name.toLowerCase() == 'director')?.star?.name;

    this.metaTagService.setMetaTags(metaTags, {
      movie: movie.title,
      director: director
    });
  }

  playTrailer(): void {
    this.videoPlayerService.playWithConsent({ content: this.movie, category: 'MovieDetail' });

    this.trackingService.trackEvent({
      action: 'ChangeTab',
      properties: {
        category: 'MovieDetail',
        label: `${this.movie.title} @ watch-trailer`,
        movie: this.movie.title,
        idMovie: this.movie.id,
        imdb: this.movie?.imdb,
        tagsMovie: getMovieTags(this.movie),
        genresMovie: getMovieGenres(this.movie)
      }
    });
  }

  goTo(sectionId: string) {
    if (!sectionId || !this.isBrowser) return;
    const sections = sectionId.split(',');
    sectionId = sections.find(s => document?.getElementById(s) != null) || sections[0];

    if (sectionId == 'all' || sectionId == 'all?') return;
    const section = document?.getElementById(sectionId);
    void this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: { section: sectionId },
        queryParamsHandling: 'merge',
        replaceUrl: true
      })
      .then(() => {
        if (section) {
          setTimeout(() => this.scrollToSection(sectionId), 0);
        }
      });

    this.tab = sectionId;

    this.trackingService.trackEvent({
      action: 'MovieMenu',
      properties: {
        category: 'MovieDetail',
        label: sectionId,
        movie: this.movie.title,
        idMovie: this.movie.id,
        imdb: this.movie?.imdb,
        tagsMovie: getMovieTags(this.movie),
        genresMovie: getMovieGenres(this.movie)
      }
    });
  }

  competitionSelected(): void {
    this.competitionsService.getCompetition(this.movie.id).subscribe(competition => {
      this.trackingService.trackEvent({
        action: 'SelectCompetition',
        properties: {
          category: 'Competitions',
          label: `${competition.title} @ ${this.movie.title}`,
          movie: this.movie.title,
          idMovie: this.movie.id,
          imdb: this.movie?.imdb,
          tagsMovie: getMovieTags(this.movie),
          genresMovie: getMovieGenres(this.movie),
          competitionTitle: competition.title,
          idCompetition: competition.id
        }
      });
    });
  }

  get templateRef(): TemplateRef<any> {
    if (this.mobile) {
      return this.verticalTemp;
    }
    switch (this.template) {
      case 'default':
        return this.horizontalTemp;
      case 'horizontal':
        return this.horizontalTemp;
      case 'vertical':
        return this.verticalTemp;
      default:
        return this.horizontalTemp;
    }
  }

  get fontPath() {
    return `assets/fonts/${this.customMovie.font}`;
  }

  get fontBoldPath() {
    return `assets/fonts/${this.customMovie.fontBold}`;
  }

  get directors() {
    return this.movie?.movieCrew
      .filter(crew => crew.crewRole.crewDepartment.id === 1)
      .map(crew => crew.star.name)
      .join(', ');
  }

  get stars() {
    return this.movie?.movieCrew
      .filter(crew => crew.crewRole.crewDepartment.id === 12)
      .map(crew => crew.star.name)
      .join(', ');
  }

  // this method will be used by the form 'demand it'
  get consumerLead(): ConsumerLead {
    const consumerLead: ConsumerLead = {};
    consumerLead.type = 'demandIt';
    if (this.locationService.userLocation) {
      consumerLead.longitude = this.locationService.userLocation.longitude;
      consumerLead.latitude = this.locationService.userLocation.latitude;
    }

    consumerLead.movie = this.movie.id;
    consumerLead.demandedFrom = 'Cinemas';
    return consumerLead;
  }

  showsLoaded(num: number): void {
    if (num > 0) {
      this.showtimesFound = true;
    } else {
      this.showtimesFound = false;
    }
  }

  downloadImage(imageUrl, orientation) {
    const imgName = this.getLastChunkOfString(imageUrl);
    fetch(imageUrl)
      .then(response => response.blob())
      .then(blob => {
        const blobURL = URL.createObjectURL(blob);

        // const link = document.createElement('a');
        // link.href = blobURL;
        // link.download = imgName;
        // link.click();
        this.trackingService.trackEvent({
          action: 'WallpaperDownloaded',
          properties: {
            category: 'MovieDetail',
            label: imgName,
            orientation: orientation,
            movie: this.movie.title,
            idMovie: this.movie.id,
            imdb: this.movie?.imdb,
            tagsMovie: getMovieTags(this.movie),
            genresMovie: getMovieGenres(this.movie)
          }
        });
        URL.revokeObjectURL(blobURL);
      })
      .catch(error => {
        console.error('Error downloading image:', error);
      });
  }

  provideAvailablePageLinksToMobileMenu(): void {
    this.hiddenBrandedSections = this.customMovie?.hideBrandedSections || [];
    this.hiddenShowtimesTabs = this.customMovie?.hideShowtimesTabs || [];

    const brandedPageLinks: PageLink[] = [];

    if (!this.hiddenBrandedSections?.includes('in-cinemas') && this.showtimesAvailable) {
      brandedPageLinks.push({
        name: 'showtimes',
        isActive: false,
        label: this.getMovieMenuGetTicketOrStreamingTranslation()
      });
    }

    if (!this.hiddenBrandedSections?.includes('reasons-to-watch') && this.movie.trReasonsToWatch) {
      brandedPageLinks.push({
        name: 'reasonsToWatch',
        isActive: false,
        label: $localize`:@@movie.menu.reasonsToWatch:Reasons to Watch`
      });
    }

    if (!this.hiddenBrandedSections?.includes('synopsis-and-cast') && this.movie.description) {
      brandedPageLinks.push({
        name: 'about',
        isActive: false,
        label: $localize`:@@movie.menu.about:About`
      });
    }

    if (!this.hiddenBrandedSections?.includes('reviews') && this.hasReviews) {
      brandedPageLinks.push({
        name: 'reviews',
        isActive: false,
        label: $localize`:@@movie.menu.reviews:Reviews`
      });
    }

    if (this.desktopWallpapers?.enabled || this.mobileWallpapers?.enabled) {
      brandedPageLinks.push({
        name: 'wallpaper',
        isActive: false,
        label: $localize`:@@movie.menu.wallpaper:Wallpapers`
      });
    }

    if (this.movie?.movieImages || this.movie?.moviePosters) {
      brandedPageLinks.push({
        name: 'gallery',
        isActive: false,
        label: $localize`:@@movie.menu.gallery:Gallery`
      });
    }

    if (this.movie?.movieVideos) {
      brandedPageLinks.push({
        name: 'videos',
        isActive: false,
        label: $localize`:@@movie.menu.videos:Videos`
      });
    }

    if (this.customMovie.festivals) {
      brandedPageLinks.push({
        name: 'festivals',
        isActive: false,
        label: $localize`:@@movie.menu.festivals:Festivals`
      });
    }

    if (this.customMovie.channelSocialFeed && this.socialFeedFound) {
      brandedPageLinks.push({
        name: 'socialFeed',
        isActive: false,
        label: $localize`:@@movie.menu.socialFeed:Social Feed`
      });
    }

    if (this.customMovie.contact) {
      brandedPageLinks.push({
        name: 'contact',
        isActive: false,
        label: $localize`:@@movie.menu.contact:Contact`
      });
    }

    //  if (this.hasCompetitions && !this.competitionBanner) {}

    // if there is extratabs
    if (this.extraTabs) {
      this.extraTabs.forEach(tab => {
        brandedPageLinks.push({
          name: tab.key,
          isActive: false,
          extraSection: true,
          label: tab.key,
          link: tab.url,
          isExternal: tab.external
        });
      });
    }
    // to get params of the movie from a service
    this.brandedThemeService.setBrandedMovie(this.movie);
    // telling to the theme service that the header will be for a branded page by providing brandedPageLinks
    this.brandedThemeService.setBrandedPageLinks(brandedPageLinks);

    if (this.customMovie.customMobileMenuStyle) {
      this.brandedThemeService.setBrandedMobileMenuStyle(this.customMovie.customMobileMenuStyle);
    }

    // subscribing to sectionSelected to see what section was selected from header mobile
    this.brandedThemeService.getSectionSelected().subscribe(section => {
      // apply change if different to empty
      const selected = brandedPageLinks.find(link => link.name === section);
      if (!this.isBrowser) return;
      if (selected) this.gotoSection(selected);
    });
  }

  isExternalLink(link: string): boolean {
    return link.indexOf('http') == 0;
  }

  public gotoSection(link: PageLink): void {
    const section = link.name;

    this.goTo(section);
  }

  scrollToSection(sectionId: string) {
    if (sectionId != 'all') {
      const section = document.getElementById(sectionId);
      if (section) {
        if (window.getComputedStyle(document.querySelector('.header-wrapper')).position === 'fixed') {
          const headerOffsetTop = document.querySelector<HTMLElement>('.page-wrapper ').offsetTop;
          const top = section.getBoundingClientRect().top + window.scrollY - headerOffsetTop;
          window.scrollTo({ top, behavior: 'smooth' });
        } else {
          section.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
      }
    }
  }

  setStreamingAvailability() {
    this.streamingAvailable =
      this.showtimeStreamingService.determineStreamingAvailability(this.movie) &&
      !this.customMovie.hideBrandedSections?.includes('watch-at-home');
  }

  setShowtimesAvailability() {
    this.determineInCinemasVisibility(this.movie);
  }

  determineInCinemasVisibility(movie: Movie) {
    this.showtimesAvailable = this.showtimeStreamingService.determineInCinemasAvailability(movie);
    // if the showtitle always then make the in cinemas component available
    if (this.env.showShowtimesComponentAlways && this.channelService.channel.userRoles?.includes(UserRole.showtimes))
      this.showtimesAvailable = true;
  }

  onHideCompetitionBanner() {
    this.hideCompetitionBtn = true;
  }

  /**
   * Method that will track the StreamingLoaded event if there are streams present.
   */
  onStreamingPlatformsLoaded(movie: Movie) {
    if (!this.streamingPlatformsLoadedTracked) {
      this.streamingPlatformsLoadedTracked = true;
      this.trackingService.trackEvent({
        action: 'StreamingLoaded',
        properties: {
          category: 'MovieStreaming',
          label: `${movie.title}`,
          movie: movie.title,
          idMovie: movie.id,
          imdb: movie?.imdb,
          tagsMovie: getMovieTags(this.movie),
          genresMovie: getMovieGenres(this.movie)
        }
      });
    }
  }

  getMovieMenuGetTicketOrStreamingTranslation() {
    const getTicketTranslation =
      this.customMovie?.customTranslations?.movieMenuGetTicket ?? $localize`:@@movie.menu.getTicket:Get Ticket`;

    return this.showtimesAvailable
      ? getTicketTranslation
      : this.streamingAvailable
        ? $localize`:@@movie.menu.getStreaming:Watch at Home`
        : getTicketTranslation;
  }

  handleSeeMoreClicked() {
    //todo: do we really need thiss?
    // const el = document.querySelector('.branded-content') as HTMLElement;
    // window.scrollTo({ top: el.offsetTop, behavior: 'smooth' });
  }

  getLastChunkOfString(url) {
    const chunks = url.split('/');
    return chunks.pop();
  }

  getCustomResponsive() {
    return this.customMovie?.customResponsive;
  }

  get isBrowser() {
    return isPlatformBrowser(this.platform);
  }

  getWallpaperSwiperConfig(orientation: 'landscape' | 'portrait'): SwiperOptions {
    return {
      slidesPerView: orientation === 'landscape' ? 1.4 : 2.4,
      spaceBetween: 10,
      scrollbar: true,
      breakpoints: {
        // when window width is >= 480px
        480: {
          slidesPerView: orientation === 'landscape' ? 2.4 : 3.4,
          spaceBetween: 20
        },
        1024: {
          slidesPerView: orientation === 'landscape' ? 3 : 5,
          spaceBetween: 30
        },
        1400: {
          slidesPerView: orientation === 'landscape' ? 4 : 6,
          spaceBetween: 30
        }
      },
      navigation: {
        nextEl: '.gallery-' + orientation + '-right',
        prevEl: '.gallery-' + orientation + '-left'
      }
    };
  }

  setAppLogoParams() {
    const logoConfig = this.env?.logosConfig?.header;
    const { width, height } = logoConfig || {};
    this.appLogoParams = { width, height };
  }

  getHorizontalGallerySwiperConfig() {
    return {
      0: 1,
      480: 1,
      1024: 1,
      1400: 1
    };
  }
}
